// src/components/GameCard.js
import React from 'react';
import VoteButtons from './VoteButtons';
import { useNavigate, useLocation } from 'react-router-dom';
import { getGameSlug } from '../utils/slugHelper';

/**
 * Reusable component for displaying game cards across the application
 * 
 * @param {Object} props
 * @param {Object} props.game - The game object with details
 * @param {Function} props.onCardClick - Function to call when card is clicked
 * @param {Function} props.onVote - Function to handle voting
 * @param {Function} props.showLogin - Function to show login modal
 * @param {Boolean} props.showVotes - Whether to show vote buttons (default: true)
 * @param {Boolean} props.showDetails - Whether to show game details (default: true)
 * @param {React.ReactNode} props.badgeContent - Optional content to show in the top-right badge
 */



const GameCard = ({ 
  game, 
  onCardClick, 
  onVote, 
  showLogin, 
  showVotes = true,
  showDetails = true,
  badgeContent
}) => {
  // Helper function to map weight to difficulty
  const mapWeightToDifficulty = (weight) => {
    if (!weight) return '';
    
    if (weight === 'Light' || weight === 'Medium Light' || weight === 'Light Medium') {
      return 'Easy';
    } else if (weight === 'Medium') {
      return 'Medium';
    } else if (weight === 'Medium Heavy' || weight === 'Heavy') {
      return 'Hard';
    }
    
    return '';
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Handle null game object
  if (!game) return null;

  // Normalize ID field (some places use id, others use game_id)
  const gameId = game.id || game.game_id;

  return (
    <div className="bg-gray-800 rounded-lg shadow-md border border-gray-700 p-6 hover:border-blue-500 transition-all relative overflow-visible">
    
      {/* Card content */}
      <div 
        className="cursor-pointer"
        onClick={(e) => {
          // Check if it's a direct click (not from vote buttons)
          if (e.target === e.currentTarget || !e.target.closest('button')) {
            // If user is holding Ctrl/Cmd or middle-clicking, open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              window.open(`/game/${getGameSlug(game)}`, '_blank');
            } else {
              // Get the current displayed games from the URL's location state if possible
              let currentResults = null;
              if (window.displayedGames && Array.isArray(window.displayedGames)) {
                currentResults = window.displayedGames;
              }
              
              // Normal click - always use direct navigation with state preservation
              navigate(`/game/${getGameSlug(game)}`, { 
                state: { 
                  from: location.pathname,
                  // Save both the pathname AND the full search string
                  search: location.search,
                  // Add complete searchParams as an additional backup
                  fullSearchParams: window.location.href.split('?')[1] || '',
                  // Save the search results
                  savedResults: currentResults
                }
              });
            }
          }
        }}
      >
        {/* Thumbnail image */}
        <div className="w-full h-[160px] mb-4 overflow-hidden rounded-md bg-gray-700 flex items-center justify-center relative">
          <img 
            src={game.thumbnail ? `/media/small/${game.thumbnail}` : "/media/placeholder.gif"}
            alt={`${game.title} board game`}
            className="w-full h-auto max-h-[160px] object-cover"
          />
          
          {/* Vote buttons overlay */}
          {showVotes && (
            <div 
              onClick={(e) => e.stopPropagation()} 
              className="absolute top-0 right-0 flex flex-col bg-black bg-opacity-60 rounded-bl-md p-1 space-y-1"
            >
              <VoteButtons 
                game={{ ...game, id: gameId }} 
                onVote={onVote} 
                showLogin={showLogin} 
                vertical={true}
              />
            </div>
          )}
        </div>

        {/* Title and description */}
        <h2 className="text-xl font-bold mb-2 text-blue-400">{game.title}</h2>
        <p className="text-gray-300 mb-4">{game.description_short}</p>
        
        {/* Game details (optional) */}
        {showDetails && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <span className="font-semibold text-gray-200">Players: </span> 
              <span className="text-gray-300">
                {game.min_players === game.max_players 
                  ? game.min_players 
                  : `${game.min_players}-${game.max_players}`}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-200">Time: </span> 
              <span className="text-gray-300">
                {game.min_play_time === game.max_play_time 
                  ? `about ${game.min_play_time} min` 
                  : `${game.min_play_time}-${game.max_play_time} min`}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-200">Age: </span> 
              <span className="text-gray-300">{game.min_age}+</span>
            </div>
            <div>
              <span className="font-semibold text-gray-200">Difficulty: </span> 
              <span className="text-gray-300">{mapWeightToDifficulty(game.weight)}</span>
            </div>
            
            {/* Themed tags - now full width instead of within columns */}
            <div className="col-span-2 mt-2">
              {game.themes && game.themes.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {game.themes.slice(0, 3).map(theme => (
                    <span key={theme} className="bg-blue-900 text-blue-100 px-2 py-0.5 rounded-full text-xs">
                      {theme}
                    </span>
                  ))}
                  {game.themes.length > 3 && (
                    <span className="text-gray-400 text-xs">+{game.themes.length - 3} more</span>
                  )}
                </div>
              )}

              {/* Mechanics tags */}
              {game.mechanics && game.mechanics.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {game.mechanics.slice(0, 2).map(mechanic => (
                    <span key={mechanic} className="bg-green-900 text-green-100 px-2 py-0.5 rounded-full text-xs">
                      {mechanic}
                    </span>
                  ))}
                  {game.mechanics.length > 2 && (
                    <span className="text-gray-400 text-xs">+{game.mechanics.length - 2} more</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCard;